import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { getCustomerById, updateCustomer, deleteCustomer } from "../../services/customerService";
import { getCustomerGroups } from "../../services/customerGroupService"; // ✅ Import Group Service

const EditCustomerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get customer from location state (if available)
  const [customer, setCustomer] = useState(location.state?.customer || null);
  const [loading, setLoading] = useState(!customer);
  const [groups, setGroups] = useState([]); // ✅ State to store groups
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    customer_type: "regular",
    group_id: "",
    delivery_schedule: "daily",
    security_amount: "",
    advance_amount: "",
    opening_due: "",
    due_amount: "",
    pet_container_balance: "",
    cool_container_balance: "",
  });

  // Fetch customer details if not available in state
  useEffect(() => {
    if (!customer) {
      fetchCustomerDetails();
    } else {
      prefillForm(customer);
    }
    fetchGroups(); // ✅ Fetch groups
  }, [id, customer]);

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getCustomerById(id);
      setCustomer(response.data);
      prefillForm(response.data);
    } catch (error) {
      console.error("Error fetching customer details:", error.message);
      toast.error("Failed to load customer details.");
      navigate(-1); // Go back if error
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await getCustomerGroups();
      setGroups(response.data);
    } catch (error) {
      console.error("Error fetching groups:", error.message);
      toast.error("Failed to load groups.");
    }
  };


  const prefillForm = (customer) => {
    setFormData({
      name: customer.name || "",
      mobile: customer.mobile || "",
      address: customer.address || "",
      customer_type: customer.customer_type || "regular",
      group_id: customer.group_id || "",
      delivery_schedule: customer.delivery_schedule || "daily",
      security_amount: customer.security_amount || 0,
      advance_amount: customer.advance_amount || 0,
      opening_due: customer.opening_due || 0,
      due_amount: customer.due_amount || 0,
    pet_container_balance: customer.pet_container_balance || 0,
    cool_container_balance: customer.cool_container_balance || 0,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveChanges = async () => {
    try {
      const updatedData = {
        name: formData.name,
        mobile: formData.mobile,
        address: formData.address,
        customer_type: formData.customer_type,
        group_id: parseInt(formData.group_id) || null,
        delivery_schedule: formData.delivery_schedule,
        security_amount: parseFloat(formData.security_amount) || 0,
        advance_amount: parseFloat(formData.advance_amount) || 0,
        opening_due: parseFloat(formData.opening_due) || 0,
        due_amount: parseFloat(formData.due_amount) || 0,
        pet_container_balance: parseFloat(formData.pet_container_balance) || 0,
        cool_container_balance: parseFloat(formData.cool_container_balance) || 0,
        created_by: 1, // Dummy user ID
      };

      await updateCustomer(id, updatedData);
      setCustomer(updatedData); // Update customer state
      toast.success("Customer details updated successfully!");
      // ✅ Navigate back and pass updated data
      navigate(-1, { state: { updatedCustomer: updatedData } });
    } catch (error) {
      console.error("Error updating customer:", error.message);
      toast.error("Failed to update customer details.");
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await deleteCustomer(id);
      toast.success("Customer deleted successfully!");
      navigate("/customers"); // Redirect to customer list after deletion
    } catch (error) {
      toast.error("Failed to delete customer.");
    } finally {
      setShowDeleteModal(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4">
      <div className="w-full max-w-md bg-white shadow-md rounded-2xl p-4 mb-8">
        <h2 className="text-xl font-bold text-gray-800 text-center mb-4">
          ✏️ Edit Customer Profile
        </h2>

        {/* Group Name Dropdown */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Group
        </label>
        <select
          name="group_id"
          value={formData.group_id}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none mb-3"
        >
          <option value="">Select Group</option>
          {groups.map((group) => (
            <option key={group.group_id} value={group.group_id}>
              {group.group_name}
            </option>
          ))}
        </select>

        {/* Name */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

        {/* Mobile */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Mobile
        </label>
        <input
          type="text"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

        {/* Address */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Address
        </label>
        <textarea
          name="address"
          value={formData.address}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        ></textarea>

        {/* Customer Type */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Customer Type
        </label>
        <select
          name="customer_type"
          value={formData.customer_type}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        >
          <option value="regular">Regular</option>
          <option value="business">Business</option>
        </select>

       

        {/* Delivery Schedule */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Delivery Schedule
        </label>
        <select
          name="delivery_schedule"
          value={formData.delivery_schedule}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        >
          <option value="daily">Daily</option>
          <option value="alternate">Alternate Days</option>
          <option value="weekly">Weekly</option>
        </select>

        {/* Security Amount */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Security Amount (₹)
        </label>
        <input
          type="number"
          name="security_amount"
          value={formData.security_amount}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

        {/* Advance Amount */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Advance Amount (₹)
        </label>
        <input
          type="number"
          name="advance_amount"
          value={formData.advance_amount}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

        {/* opening_due */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Opening Due (₹)
        </label>
        <input
          type="number"
          name="opening_due"
          value={formData.opening_due}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

        {/* Due Amount */}
        <label className="block text-gray-600 text-sm font-semibold mb-1">
          Due Amount (₹)
        </label>
        <input
          type="number"
          name="due_amount"
          value={formData.due_amount}
          onChange={handleChange}
          className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
        />

           {/* Cool & Pet Bottle Balance */}
           <div className="flex space-x-4">
                <div className="mb-4 w-1/2">
                  <label className="block text-sm font-medium mb-1">
                    Cool Balance
                  </label>
                  <input
                    type="number"
                    name="cool_container_balance"
                    value={formData.cool_container_balance}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Cool Balance"
                  />
                </div>
                <div className="mb-4 w-1/2">
                  <label className="block text-sm font-medium mb-1">
                    Pet Balance
                  </label>
                  <input
                    type="number"
                    name="pet_container_balance"
                    value={formData.pet_container_balance}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Pet Balance"
                  />
                </div>
              </div>

        {/* Buttons */}
        <div className="flex justify-between mt-4">
          
        
          
          <button onClick={() => navigate(-1)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400">
            Cancel
          </button>


          <button onClick={handleSaveChanges} className="bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600">
            Save Changes
          </button>

          
        </div>

        
        {/* Delete Customer Button */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600"
          >
            Delete Customer
          </button>
        </div>

         {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold text-gray-800 text-center">
              ⚠️ Confirm Deletion
            </h2>
            <p className="text-center text-gray-600 mt-2">
              Are you sure you want to delete this customer?
            </p>
            <div className="flex justify-center mt-4 space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCustomer}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default EditCustomerProfile;
